import { useEffect, useState } from 'react'
import {
  api,
  ErrorDeApi,
  type CuentaBancaria,
  type PartidasEnConciliacion as Partidas,
  type SemaforoDeCumplimiento,
} from '../servicios/api'
import { fechaContable } from '../fechas'

const pesos = (n: number) => n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Con signo: positivo entró al banco, negativo salió. */
const conSigno = (n: number) => (n < 0 ? `−${pesos(-n)}` : pesos(n))

/** Las mismas insignias del semáforo que usa el tablero. */
const INSIGNIA: Record<SemaforoDeCumplimiento, string> = {
  AlCorriente: 'vigente',
  PorVencer: 'aviso',
  Vencido: 'vencido',
}

/**
 * Las partidas en conciliación de la cuenta a una fecha de corte, por tramos de antigüedad con su
 * semáforo (RF-1309): movimientos del banco sin póliza y movimientos de póliza sin reflejo en el
 * banco. Una partida vieja casi siempre es un error de registro o un cheque que nunca se cobró.
 */
export function PartidasEnConciliacion({ clienteId, cuenta }: { clienteId: string; cuenta: CuentaBancaria }) {
  const [corte, setCorte] = useState('')
  const [partidas, setPartidas] = useState<Partidas>()
  const [error, setError] = useState('')

  async function cargar(c = corte) {
    setError('')
    try {
      setPartidas(await api.partidasEnConciliacion(clienteId, cuenta.id, c || undefined))
    } catch (e) {
      setPartidas(undefined)
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudieron cargar las partidas en conciliación de la cuenta.')
    }
  }

  useEffect(() => {
    void cargar('')
  }, [clienteId, cuenta.id])

  function aplicar(evento: React.FormEvent) {
    evento.preventDefault()
    void cargar()
  }

  return (
    <section aria-labelledby="titulo-partidas-en-conciliacion">
      <h2 id="titulo-partidas-en-conciliacion">Partidas en conciliación y su antigüedad</h2>

      <form className="filtros" onSubmit={aplicar} aria-label="Fecha de corte de las partidas en conciliación">
        <label>Fecha de corte <input type="date" value={corte} onChange={(e) => setCorte(e.target.value)} /></label>
        <button type="submit" className="secundario">Aplicar</button>
        {!corte && <span className="sutil">Sin fecha, el corte es hoy.</span>}
      </form>

      {error && <p className="error" role="alert">{error}</p>}
      {!partidas && !error && <p className="sutil">Cargando…</p>}

      {partidas && (
        <>
          <p className="sutil">Al {fechaContable(partidas.corte)}.</p>
          <div className="tabla-envoltura">
            <table className="tabla" aria-label="Partidas en conciliación por antigüedad">
              <thead>
                <tr>
                  <th scope="col">Antigüedad</th>
                  <th scope="col" className="num">Partidas</th>
                  <th scope="col" className="num">Importe</th>
                </tr>
              </thead>
              <tbody>
                {partidas.tramos.map((t) => (
                  <tr key={t.etiqueta}>
                    <td><span className={`insignia ${INSIGNIA[t.semaforo]}`}>{t.etiqueta} días</span></td>
                    <td className="num">{t.cantidad}</td>
                    <td className="num">{conSigno(t.importe)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {partidas.partidas.length === 0 ? (
            <p className="vacio">
              No hay partidas en conciliación al {fechaContable(partidas.corte)}: todo lo del banco y lo de{' '}
              {cuenta.cuentaContable} está emparejado.
            </p>
          ) : (
            <div className="tabla-envoltura">
              <table className="tabla" aria-label="Partidas en conciliación">
                <thead>
                  <tr>
                    <th scope="col">Lado</th>
                    <th scope="col">Fecha</th>
                    <th scope="col">Concepto</th>
                    <th scope="col" className="num">Importe</th>
                    <th scope="col" className="num">Días</th>
                    <th scope="col">Tramo</th>
                  </tr>
                </thead>
                <tbody>
                  {partidas.partidas.map((p) => (
                    <tr key={`${p.lado}-${p.id}`}>
                      <td>{p.lado === 'Banco' ? 'Banco sin póliza' : 'Póliza sin banco'}</td>
                      <td>{fechaContable(p.fecha)}</td>
                      <td>
                        {p.tipoPoliza && p.folio !== null && <>Póliza de {p.tipoPoliza} {p.folio} · </>}
                        {p.concepto}
                        {p.nota && <div className="sutil" style={{ fontSize: 11 }}>{p.nota}</div>}
                      </td>
                      <td className="num">{conSigno(p.importe)}</td>
                      <td className="num">{p.dias}</td>
                      <td><span className={`insignia ${INSIGNIA[p.semaforo]}`}>{p.tramo}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  )
}
